import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text
} from '@chakra-ui/react'
import { memo, ReactNode, useEffect, useRef, useState } from 'react'
import ColorPicker, { useColorPicker } from 'react-best-gradient-color-picker'

import { Icon } from '../Icon'

interface IGradientBuilderProps {
  value: string
  onChange: (value: string) => void
}

const MONO = "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, monospace"

const GRADIENT_PRESETS = [
  'linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(103,2,142,1) 50%, rgba(250,0,105,1) 100%)',
  'linear-gradient(90deg,rgba(2, 0, 36, 1) 0%, rgba(9, 9, 121, 1) 35%, rgba(0, 212, 255, 1) 100%)',
  'linear-gradient(0deg,rgba(34, 193, 195, 1) 0%, rgba(253, 187, 45, 1) 100%)',
  'radial-gradient(circle,rgba(63, 94, 251, 1) 0%, rgba(252, 70, 107, 1) 100%)',
  'linear-gradient(90deg,rgba(131, 58, 180, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 176, 69, 1) 100%)',
  'radial-gradient(circle,rgba(238, 174, 202, 1) 0%, rgba(148, 187, 233, 1) 100%)'
]

const LOCALES = {
  CONTROLS: {
    SOLID: 'Sólido',
    GRADIENT: 'Gradiente'
  }
}

const ANGLE_PRESETS = [0, 45, 90, 135, 180, 225, 270]

function normalizeHex(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  if (/^#([0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(withHash)) return withHash
  return null
}

function rgbaToHex(color: string): string {
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/)
  if (!match) return color
  const r = parseInt(match[1])
  const g = parseInt(match[2])
  const b = parseInt(match[3])
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0')
  if (a < 1) return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a * 255)}`
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

interface IAngleSpinnerProps {
  angle: number
  onChange: (angle: number) => void
}

function AngleSpinner({ angle, onChange }: IAngleSpinnerProps) {
  const size = 68
  const center = size / 2
  const orbitRadius = 24
  const knobR = 6

  const angleRad = (angle * Math.PI) / 180
  const knobX = center + orbitRadius * Math.sin(angleRad)
  const knobY = center - orbitRadius * Math.cos(angleRad)

  const circleRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  const getAngleFromEvent = (e: MouseEvent): number => {
    if (!circleRef.current) return angle
    const rect = circleRef.current.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    let deg = Math.atan2(e.clientX - cx, -(e.clientY - cy)) * (180 / Math.PI)
    if (deg < 0) deg += 360
    return Math.round(deg)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isDragging.current = true

    const onMove = (me: MouseEvent) => {
      if (isDragging.current) onChange(getAngleFromEvent(me))
    }
    const onUp = () => {
      isDragging.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  return (
    <Box
      ref={circleRef}
      position="relative"
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      cursor="grab"
      flexShrink={0}
      boxShadow="inset 0 0 0 8px rgba(250, 0, 105, 0.03), 0 2px 8px -2px rgba(0,0,0,0.04)"
      _active={{ cursor: 'grabbing' }}
      onMouseDown={handleMouseDown}
    >
      {/* Tick marks at 4 cardinal points */}
      {[0, 90, 180, 270].map((tickAngle) => {
        const rad = (tickAngle * Math.PI) / 180
        const tx = center + (orbitRadius + 5) * Math.sin(rad)
        const ty = center - (orbitRadius + 5) * Math.cos(rad)
        return (
          <Box
            key={tickAngle}
            position="absolute"
            w="2px"
            h="2px"
            borderRadius="full"
            bg="gray.300"
            left={`${tx}px`}
            top={`${ty}px`}
            transform="translate(-50%, -50%)"
            pointerEvents="none"
          />
        )
      })}

      {/* Direction arm — from center to knob */}
      <Box
        position="absolute"
        left="50%"
        top="50%"
        w="1px"
        h={`${orbitRadius}px`}
        bg="magenta.400"
        opacity={0.6}
        transformOrigin="top center"
        transform={`translate(-50%, 0) rotate(${angle + 180}deg)`}
        pointerEvents="none"
      />

      {/* Orbit knob */}
      <Box
        position="absolute"
        w={`${knobR * 2}px`}
        h={`${knobR * 2}px`}
        borderRadius="full"
        bg="magenta.500"
        boxShadow="0 0 0 3px rgba(250, 0, 105, 0.15), 0 2px 4px rgba(250, 0, 105, 0.35)"
        transform="translate(-50%, -50%)"
        left={`${knobX}px`}
        top={`${knobY}px`}
        pointerEvents="none"
      />
      {/* Center dot */}
      <Box
        position="absolute"
        w="4px"
        h="4px"
        borderRadius="full"
        bg="gray.400"
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        pointerEvents="none"
      />
    </Box>
  )
}

function GradientBuilder({ value, onChange }: IGradientBuilderProps) {
  const { getGradientObject, addPoint, deletePoint, setPointLeft, setSelectedPoint, selectedPoint, handleChange } =
    useColorPicker(value, onChange)

  const gradientObject = getGradientObject(value)
  const stops = gradientObject?.colors ?? []

  const angleMatch = value.match(/linear-gradient\((\d+)deg/)
  const currentAngle = angleMatch ? Number(angleMatch[1]) : 90
  const isLinear = value.startsWith('linear-gradient')

  const applyAngle = (val: number) => {
    const clamped = Math.min(360, Math.max(0, Math.round(val)))
    const updated = value.replace(/linear-gradient\(\d+deg/, `linear-gradient(${clamped}deg`)
    onChange(updated)
  }

  const [editingHex, setEditingHex] = useState<string>('')
  const [editingLeft, setEditingLeft] = useState<string>('')

  useEffect(() => {
    setEditingHex(rgbaToHex(stops[selectedPoint]?.value ?? ''))
    setEditingLeft(String(stops[selectedPoint]?.left ?? 0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoint, stops[selectedPoint]?.value, stops[selectedPoint]?.left])

  const commitLeft = (index: number) => {
    if (selectedPoint !== index) return
    const clamped = Math.min(100, Math.max(0, Number(editingLeft) || 0))
    setEditingLeft(String(clamped))
    setPointLeft(clamped)
  }

  return (
    <Box
      borderRadius="14px"
      border="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      overflow="hidden"
      boxShadow="0 1px 2px rgba(15, 15, 30, 0.03)"
    >
      {/* ===== Header bar (design-tool style) ===== */}
      <Flex
        px={4}
        py="10px"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        align="center"
        justify="space-between"
        gap={3}
      >
        <Flex align="center" gap={2.5} minW={0}>
          <Box
            position="relative"
            w="22px"
            h="22px"
            borderRadius="5px"
            overflow="hidden"
            flexShrink={0}
            boxShadow="inset 0 0 0 1.5px white, 0 0 0 1px rgba(0,0,0,0.08)"
          >
            <Box position="absolute" inset={0} style={{ background: value }} />
          </Box>
          <Text
            fontFamily={MONO}
            fontSize="10px"
            fontWeight={600}
            color="gray.700"
            letterSpacing="0.22em"
            textTransform="uppercase"
          >
            Editor de gradiente
          </Text>
        </Flex>
        <Flex align="center" gap={2} flexShrink={0}>
          <Box
            px="8px"
            py="2px"
            borderRadius="4px"
            bg="gray.100"
            fontFamily={MONO}
            fontSize="9px"
            color="gray.600"
            letterSpacing="0.18em"
            textTransform="uppercase"
            fontWeight={600}
          >
            {isLinear ? 'Linear' : 'Radial'}
          </Box>
          <Text fontFamily={MONO} fontSize="9px" color="gray.400" letterSpacing="0.18em" textTransform="uppercase">
            {stops.length} stops
          </Text>
        </Flex>
      </Flex>

      {/* ===== Body ===== */}
      <Flex direction={{ base: 'column', md: 'row' }} gap={0} align="stretch">
        {/* ----- Left: picker ----- */}
        <Box
          bg="white"
          p={3}
          borderRight={{ base: 'none', md: '1px solid' }}
          borderBottom={{ base: '1px solid', md: 'none' }}
          borderColor={{ base: 'gray.200', md: 'gray.200' }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          flexShrink={0}
          sx={{
            '.rbgcp-control-btn, .rbgcp-control-btn-wrapper': {
              borderRadius: '6px !important'
            },
            '.rbgcp-control-btn.rbgcp-control-btn-selected': {
              backgroundColor: 'var(--chakra-colors-magenta-500) !important'
            }
          }}
        >
          <ColorPicker
            value={value}
            onChange={onChange}
            hideColorTypeBtns={true}
            hideEyeDrop={true}
            hideAdvancedSliders={true}
            hideColorGuide={true}
            hideGradientStop={true}
            hideInputType={true}
            presets={GRADIENT_PRESETS}
            locales={LOCALES}
            disableDarkMode={true}
            width={320}
          />
        </Box>

        {/* ----- Right: angle + stops ----- */}
        <Flex direction="column" flex={1} p={5} gap={6} minW={0}>
          {/* Angle control — linear gradients only */}
          {isLinear && (
            <Box>
              <ToolLabel>Ângulo</ToolLabel>
              <Flex align="center" gap={4} flexWrap="wrap">
                <AngleSpinner angle={currentAngle} onChange={applyAngle} />
                <Flex direction="column" gap={2} flex={1} minW="160px">
                  <Flex align="center" gap={2}>
                    <NumberInput
                      size="sm"
                      min={0}
                      max={360}
                      value={currentAngle}
                      onChange={(_s, val) => applyAngle(val)}
                      w="80px"
                    >
                      <NumberInputField
                        fontSize="13px"
                        fontFamily={MONO}
                        textAlign="center"
                        bg="white"
                        borderColor="gray.200"
                        fontWeight={600}
                        _focusVisible={{
                          borderColor: 'magenta.400',
                          boxShadow: '0 0 0 1px var(--chakra-colors-magenta-400)'
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontFamily={MONO} fontSize="13px" color="gray.500" fontWeight={600}>
                      °
                    </Text>
                  </Flex>
                  <Flex gap="3px" flexWrap="wrap">
                    {ANGLE_PRESETS.map((a) => {
                      const isActive = currentAngle === a
                      return (
                        <Box
                          as="button"
                          key={a}
                          px="7px"
                          py="3px"
                          fontFamily={MONO}
                          fontSize="9px"
                          fontWeight={600}
                          letterSpacing="0.05em"
                          color={isActive ? 'white' : 'gray.500'}
                          bg={isActive ? 'gray.900' : 'white'}
                          border="1px solid"
                          borderColor={isActive ? 'gray.900' : 'gray.200'}
                          borderRadius="5px"
                          transition="all 0.12s"
                          _hover={{
                            bg: isActive ? 'gray.800' : 'gray.100',
                            borderColor: isActive ? 'gray.800' : 'gray.300'
                          }}
                          onClick={() => applyAngle(a)}
                        >
                          {a}°
                        </Box>
                      )
                    })}
                  </Flex>
                </Flex>
              </Flex>
            </Box>
          )}

          {/* Stops panel */}
          <Box>
            <Flex align="center" justify="space-between" mb={3}>
              <ToolLabel mb={0}>Stops · {stops.length}</ToolLabel>
              <Button
                size="xs"
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                fontFamily={MONO}
                fontSize="9px"
                letterSpacing="0.18em"
                textTransform="uppercase"
                fontWeight={600}
                h="26px"
                px="10px"
                bg="white"
                _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                onClick={() => addPoint(50)}
              >
                + Adicionar cor
              </Button>
            </Flex>
            <Flex direction="column" gap="6px">
              {stops.map((stop: { value: string; left: number }, index: number) => {
                const isActive = selectedPoint === index
                return (
                  <Flex
                    key={index}
                    role="group"
                    align="center"
                    gap={2}
                    px={2}
                    py="8px"
                    borderRadius="9px"
                    bg={isActive ? 'white' : 'transparent'}
                    border="1px solid"
                    borderColor={isActive ? 'gray.300' : 'transparent'}
                    boxShadow={isActive ? '0 2px 4px -1px rgba(15, 15, 30, 0.08)' : 'none'}
                    transition="all 0.12s"
                    _hover={{ bg: isActive ? 'white' : 'rgba(255,255,255,0.6)' }}
                  >
                    {/* Swatch with checker bg (for transparency) */}
                    <Box
                      position="relative"
                      w="32px"
                      h="32px"
                      borderRadius="7px"
                      cursor="pointer"
                      flexShrink={0}
                      overflow="hidden"
                      onClick={() => setSelectedPoint(index)}
                      boxShadow={
                        isActive
                          ? 'inset 0 0 0 2px white, 0 0 0 2px var(--chakra-colors-magenta-400), 0 2px 4px rgba(250, 0, 105, 0.2)'
                          : 'inset 0 0 0 1.5px white, 0 0 0 1px rgba(0,0,0,0.12)'
                      }
                      transition="box-shadow 0.12s"
                    >
                      {/* Checker for alpha visibility */}
                      <Box
                        position="absolute"
                        inset={0}
                        bgImage="linear-gradient(45deg, #E2E8F0 25%, transparent 25%), linear-gradient(-45deg, #E2E8F0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #E2E8F0 75%), linear-gradient(-45deg, transparent 75%, #E2E8F0 75%)"
                        bgSize="8px 8px"
                        bgPosition="0 0, 0 4px, 4px -4px, -4px 0"
                      />
                      <Box position="absolute" inset={0} bg={stop.value} />
                    </Box>

                    {/* Hex input */}
                    <Input
                      size="sm"
                      value={isActive ? editingHex : rgbaToHex(stop.value)}
                      onFocus={() => {
                        setSelectedPoint(index)
                        setEditingHex(rgbaToHex(stop.value))
                      }}
                      onChange={(e) => {
                        if (isActive) setEditingHex(e.target.value)
                      }}
                      onBlur={() => {
                        if (!isActive) return
                        const normalized = normalizeHex(editingHex)
                        if (normalized) {
                          handleChange(normalized)
                        } else {
                          setEditingHex(rgbaToHex(stops[selectedPoint]?.value ?? ''))
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' || !isActive) return
                        const normalized = normalizeHex(editingHex)
                        if (normalized) {
                          handleChange(normalized)
                        } else {
                          setEditingHex(rgbaToHex(stops[selectedPoint]?.value ?? ''))
                        }
                      }}
                      placeholder="#000000"
                      w="102px"
                      fontFamily={MONO}
                      fontSize="12px"
                      fontWeight={500}
                      textTransform="uppercase"
                      bg="white"
                      borderColor="gray.200"
                      _focusVisible={{
                        borderColor: 'magenta.400',
                        boxShadow: '0 0 0 1px var(--chakra-colors-magenta-400)'
                      }}
                    />

                    {/* Position input */}
                    <Flex
                      align="center"
                      gap="4px"
                      px={2}
                      h="32px"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="6px"
                      w="78px"
                      _focusWithin={{
                        borderColor: 'magenta.400',
                        boxShadow: '0 0 0 1px var(--chakra-colors-magenta-400)'
                      }}
                    >
                      <Input
                        size="sm"
                        variant="unstyled"
                        value={isActive ? editingLeft : String(stop.left)}
                        onFocus={() => {
                          setSelectedPoint(index)
                          setEditingLeft(String(stop.left))
                        }}
                        onChange={(e) => {
                          if (isActive) setEditingLeft(e.target.value)
                        }}
                        onBlur={() => commitLeft(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitLeft(index)
                        }}
                        fontFamily={MONO}
                        fontSize="12px"
                        fontWeight={500}
                        textAlign="right"
                        p={0}
                      />
                      <Text fontFamily={MONO} fontSize="11px" color="gray.400" fontWeight={500}>
                        %
                      </Text>
                    </Flex>

                    {/* Delete — visible only when more than 2 stops */}
                    <Box ml="auto">
                      {stops.length > 2 ? (
                        <IconButton
                          aria-label="Remover stop"
                          icon={<Icon name="lni lni-close" size="sm" />}
                          size="xs"
                          variant="ghost"
                          color="gray.400"
                          opacity={isActive ? 1 : 0}
                          _groupHover={{ opacity: 1 }}
                          _hover={{ bg: 'red.50', color: 'red.500' }}
                          onClick={() => deletePoint(index)}
                        />
                      ) : (
                        <Box w="24px" />
                      )}
                    </Box>
                  </Flex>
                )
              })}
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

function ToolLabel({ children, mb }: { children: ReactNode; mb?: number }) {
  return (
    <Text
      fontFamily={MONO}
      fontSize="10px"
      fontWeight={600}
      color="gray.500"
      mb={mb ?? 3}
      letterSpacing="0.22em"
      textTransform="uppercase"
    >
      {children}
    </Text>
  )
}

export default memo(GradientBuilder)
